// Bulk upload: match filenames to content slots (pure functions, no React).
import JSZip from "jszip";

const EXT_TYPES = {
  mp3: "audio/mpeg", wav: "audio/wav", m4a: "audio/mp4", aac: "audio/aac", ogg: "audio/ogg", oga: "audio/ogg", flac: "audio/flac", webm: "audio/webm",
  mp4: "video/mp4", mov: "video/quicktime", m4v: "video/mp4",
  png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg",
};
const KIND_PREFIX = { audio: ["audio/"], video: ["video/"], image: ["image/png", "image/jpeg"] };
export const ACCEPT = { video: "video/*", audio: "audio/*", image: "image/png,image/jpeg" };

const QUOTES = /^["'“”‘’`\s]+|["'“”‘’`\s]+$/g;
export const cleanName = (name) => String(name).replace(QUOTES, "").replace(/["'“”‘’`]+(\.[a-z0-9]{2,5})$/i, "$1");
const extOf = (name) => (cleanName(name).match(/\.([a-z0-9]{2,5})$/i)?.[1] || "").toLowerCase();

// " happy polka music #2 " — lowercase, no extension/quotes, ElevenLabs noise and punctuation become spaces.
export function norm(name) {
  let s = cleanName(name).replace(/\.[a-z0-9]{2,5}$/i, "").replace(QUOTES, "").toLowerCase();
  s = s.replace(/elevenlabs/g, " ").replace(/[^a-z0-9#]+/g, " ").replace(/\s+/g, " ").trim();
  return ` ${s} `;
}

export function fileType(file) {
  if (file.type && file.type !== "application/octet-stream") return file.type;
  const ext = extOf(file.name);
  // .webm is audio in the radio group; video groups still accept it below.
  return EXT_TYPES[ext] || "";
}

export const kindOk = (type, kind) => KIND_PREFIX[kind].some((p) => type.startsWith(p)) || (kind === "video" && type === "audio/webm");

// Best slot for a filename: the slot whose longest match-name appears as a whole-token run inside it.
export function bestSlot(filename, slots) {
  const n = norm(filename);
  let best = null;
  for (const s of slots) {
    for (const m of s.match || [s.slot]) {
      const mn = norm(m);
      if (mn.trim() && n.includes(mn) && (!best || mn.length > best.len)) best = { slot: s.slot, len: mn.length };
    }
  }
  return best?.slot || null;
}

// Expand a dropped/selected list: .zip files are unpacked (folders flattened, macOS junk skipped).
export async function expandFiles(fileList) {
  const out = [];
  for (const f of fileList) {
    if (extOf(f.name) !== "zip") { out.push(f); continue; }
    const zip = await JSZip.loadAsync(f);
    const entries = Object.values(zip.files).filter((e) => !e.dir && !e.name.startsWith("__MACOSX/") && !e.name.split("/").pop().startsWith("."));
    for (const e of entries) {
      const base = e.name.split("/").pop();
      const blob = await e.async("blob");
      out.push(new File([blob], base, { type: EXT_TYPES[extOf(base)] || "" }));
    }
  }
  return out;
}

// One row per file: auto slot (first file wins a slot; later duplicates become unmatched) and any problem.
export function buildRows(files, slots) {
  const taken = new Set();
  return files.map((file, i) => {
    const type = fileType(file);
    const slot = bestSlot(file.name, slots);
    const spec = slots.find((s) => s.slot === slot);
    let issue = null;
    if (!slot) issue = "no matching slot";
    else if (!kindOk(type, spec.kind)) issue = `not ${spec.kind === "video" ? "a" : "an"} ${spec.kind} file`;
    else if (taken.has(slot)) issue = `duplicate — ${slot} already matched`;
    const ok = slot && !issue;
    if (ok) taken.add(slot);
    return { id: i, file, type, slot: ok ? slot : "", auto: !!ok, issue, status: "pending" };
  });
}
